/**
 * Canvas Edge Types Configuration
 *
 * Central registry for all custom edge types used in the canvas.
 * Part of PROJ-056 Advanced Canvas Features.
 *
 * @module components/canvas/edgeTypes
 */

import { BezierEdge, StraightEdge, StepEdge, SmoothStepEdge } from '@xyflow/react';
import type { EdgeTypes } from '@xyflow/react';
import { OrthogonalEdge, FloatingEdge, ReshapableEdge } from './edges';

/**
 * Available edge type identifiers
 */
export type EdgeTypeId = 'default' | 'straight' | 'step' | 'smoothstep' | 'orthogonal' | 'floating' | 'reshapable';

/**
 * Edge type configuration
 */
export interface EdgeTypeConfig {
  id: EdgeTypeId;
  name: string;
  description: string;
}

/**
 * Available edge types with descriptions
 */
export const EDGE_TYPE_OPTIONS: EdgeTypeConfig[] = [
  {
    id: 'default',
    name: 'Bezier',
    description: 'Smooth curved connection',
  },
  {
    id: 'straight',
    name: 'Straight',
    description: 'Direct line connection',
  },
  {
    id: 'step',
    name: 'Step',
    description: 'Right-angled step pattern',
  },
  {
    id: 'smoothstep',
    name: 'Smooth Step',
    description: 'Rounded step corners',
  },
  {
    id: 'orthogonal',
    name: 'Orthogonal',
    description: 'Smart right-angled routing',
  },
  {
    id: 'floating',
    name: 'Floating',
    description: 'Dynamic connection points',
  },
  {
    id: 'reshapable',
    name: 'Reshapable',
    description: 'Interactive control points',
  },
];

/**
 * Edge types registry for React Flow
 *
 * Register all custom edge components here.
 * The key matches the type property on Edge objects.
 */
export const edgeTypes = {
  default: BezierEdge,
  straight: StraightEdge,
  step: StepEdge,
  smoothstep: SmoothStepEdge,
  orthogonal: OrthogonalEdge,
  floating: FloatingEdge,
  reshapable: ReshapableEdge,
} satisfies EdgeTypes;

/**
 * Get the default edge type
 */
export function getDefaultEdgeType(): EdgeTypeId {
  return 'default';
}

/**
 * Check if a string is a valid edge type
 */
export function isValidEdgeType(type: string): type is EdgeTypeId {
  return EDGE_TYPE_OPTIONS.some((opt) => opt.id === type);
}

/**
 * Get edge type configuration by ID
 */
export function getEdgeTypeConfig(id: EdgeTypeId): EdgeTypeConfig | undefined {
  return EDGE_TYPE_OPTIONS.find((opt) => opt.id === id);
}
